import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, ArrowRight, Star, Zap, Shield, Brain } from 'lucide-react';

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const flowRight = keyframes`
  0% { transform: translateX(-8px); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateX(8px); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 151, 178, 0.3), 0 0 40px rgba(0, 151, 178, 0.1); }
  50% { box-shadow: 0 0 40px rgba(0, 151, 178, 0.6), 0 0 80px rgba(0, 151, 178, 0.2); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #1a1e2e;
  color: #FAECEC;
`;

const HeroSection = styled.section`
  padding: 120px 20px 80px;
  background: linear-gradient(180deg, #0d1117 0%, #1a1e2e 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -200px;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(0, 151, 178, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 50px;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const ProBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #000;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 24px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 60%, #00d4ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.7;
  color: rgba(250, 236, 236, 0.8);
  max-width: 700px;
  margin-bottom: 48px;
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 16px 32px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 151, 178, 0.4);
  animation: ${glowPulse} 3s ease-in-out infinite;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 151, 178, 0.5);
    color: #FAECEC;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  color: #FAECEC;
  padding: 16px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1.5px solid rgba(0, 151, 178, 0.5);

  &:hover {
    border-color: #0097B2;
    background: rgba(0, 151, 178, 0.1);
    transform: translateY(-3px);
    color: #FAECEC;
  }
`;

const ScreenshotSection = styled.section`
  padding: 0 20px 100px;
  background: #1a1e2e;
`;

const ScreenshotWrapper = styled(motion.div)`
  max-width: 1100px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 151, 178, 0.25);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5), 0 0 60px rgba(0, 151, 178, 0.1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(0, 151, 178, 0.3), transparent 40%, transparent 60%, rgba(0, 151, 178, 0.15));
    pointer-events: none;
    z-index: 1;
  }
`;

const Screenshot = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const ArchSection = styled.section`
  padding: 100px 20px;
  background: #0d1117;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FlowDiagram = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const AgentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
`;

const AgentNode = styled(motion.div)`
  background: ${props => props.bg || 'rgba(0, 151, 178, 0.1)'};
  border: 1px solid ${props => props.border || 'rgba(0, 151, 178, 0.3)'};
  border-radius: 14px;
  padding: 14px 20px;
  min-width: 180px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.accentColor || '#0097B2'};
    border-radius: 14px 14px 0 0;
  }
`;

const NodeTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #FAECEC;
  margin-bottom: 4px;
`;

const NodeSubtitle = styled.div`
  font-size: 0.75rem;
  color: rgba(250, 236, 236, 0.5);
`;

const NodeParam = styled.div`
  font-size: 0.7rem;
  color: rgba(250, 236, 236, 0.4);
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 8px;
  font-family: 'Courier New', monospace;
`;

const ArrowSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  color: rgba(0, 151, 178, 0.6);
  position: relative;
  width: 60px;

  svg {
    animation: ${flowRight} 1.5s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    transform: rotate(90deg);
    width: auto;
    padding: 8px;
  }
`;

const ConsensusNode = styled(motion.div)`
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%);
  border: 2px solid rgba(245, 158, 11, 0.5);
  border-radius: 16px;
  padding: 20px 28px;
  min-width: 200px;
  text-align: center;
  flex-shrink: 0;
  animation: ${pulse} 2.5s ease-in-out infinite;
`;

const ConsensusTitle = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #f59e0b;
  margin-bottom: 4px;
`;

const ConsensusSubtitle = styled.div`
  font-size: 0.75rem;
  color: rgba(245, 158, 11, 0.7);
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1100px;
  margin: 60px auto 0;
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.08) 0%, rgba(250, 236, 236, 0.03) 100%);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  padding: 36px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(0, 151, 178, 0.4);
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(0, 151, 178, 0.15);
  }
`;

const FeatureIconWrap = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: #FAECEC;
  margin-bottom: 12px;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  color: rgba(250, 236, 236, 0.7);
  line-height: 1.6;
`;

const ProCTASection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #0d1117 0%, #1a1e2e 100%);
  text-align: center;
`;

const ProCTATitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #FAECEC 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ProCTASubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(250, 236, 236, 0.7);
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const HeaderBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 20px;
  background: rgba(13, 17, 23, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 151, 178, 0.1);
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled(Link)`
  text-decoration: none;
`;

const HeaderCTA = styled.a`
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 10px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 151, 178, 0.4);
    color: #FAECEC;
  }
`;

const ConsensusAgentPage = () => {
  const features = [
    {
      icon: <Brain size={24} color="#FAECEC" />,
      title: "4 AI Models Compete",
      desc: "Claude (Anthropic), Gemini (Google), GPT (OpenAI), and Grok (xAI) simultaneously analyze real-time market data and generate independent trading recommendations."
    },
    {
      icon: <Zap size={24} color="#FAECEC" />,
      title: "Consensus Judge",
      desc: "The Consensus Agent evaluates all 4 recommendations, identifies patterns, resolves conflicts, and makes the final autonomous trading decision with maximum confidence."
    },
    {
      icon: <Shield size={24} color="#FAECEC" />,
      title: "Autonomous Execution",
      desc: "The final decision is automatically executed on your connected exchange — Bybit, Binance, OKX and more — with configurable leverage and risk parameters."
    },
    {
      icon: <Star size={24} color="#FAECEC" />,
      title: "Real-Time Market Data",
      desc: "WebSocket streams deliver live price data, order book depth, and OHLCV candles. Chart analysis with RSI, MACD and more is included in every cycle."
    }
  ];

  return (
    <>
      <Helmet>
        <title>f01i Consensus Agent — 4 AI Models, One Trading Decision</title>
        <meta name="description" content="f01i Consensus Agent uses Claude, Gemini, GPT and Grok simultaneously to analyze real-time market data. The Consensus Judge makes the final autonomous trading decision. Available for Pro plans." />
        <link rel="canonical" href="https://f01i.ai/consensusagent" />
      </Helmet>

      <HeaderBar>
        <HeaderInner>
          <LogoLink to="/">
            <img src="/f01i_logo.png" alt="f01i.ai" style={{ height: '36px', width: 'auto' }} />
          </LogoLink>
          <HeaderCTA href="https://f01i.app/login" target="_blank" rel="noopener noreferrer">
            Try Now
          </HeaderCTA>
        </HeaderInner>
      </HeaderBar>

      <PageContainer>
        <HeroSection>
          <Container>
            <BackLink to="/">
              <ArrowLeft size={18} />
              Back to Home
            </BackLink>

            <ProBadge
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Star size={12} fill="#000" />
              Pro Plans Only
            </ProBadge>

            <HeroTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              f01i Consensus Agent
            </HeroTitle>

            <HeroSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              The most advanced f01i Agent. 4 top AI models simultaneously analyze real-time market data. 
              The Consensus Agent judges their recommendations, analyzes patterns, and makes the final 
              autonomous trading decision.
            </HeroSubtitle>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <CTAGroup>
                <PrimaryButton
                  href="https://f01i.app/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Start with Consensus Agent
                  <ArrowRight size={18} />
                </PrimaryButton>
                <SecondaryButton
                  href="https://f01i.app/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  View Documentation
                </SecondaryButton>
              </CTAGroup>
            </motion.div>
          </Container>
        </HeroSection>

        <ScreenshotSection>
          <Container>
            <ScreenshotWrapper
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
            >
              <Screenshot
                src="/consensus_agent.webp"
                alt="f01i Consensus Agent — node diagram showing 4 AI models feeding into Consensus Judge"
                loading="lazy"
              />
            </ScreenshotWrapper>
          </Container>
        </ScreenshotSection>

        <ArchSection>
          <Container>
            <SectionTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              How the Consensus Agent Works
            </SectionTitle>

            <FlowDiagram>
              <AgentColumn>
                <AgentNode
                  bg="rgba(255, 140, 100, 0.08)"
                  border="rgba(255, 140, 100, 0.3)"
                  accentColor="#ff8c64"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <NodeTitle>Claude (Anthropic)</NodeTitle>
                  <NodeSubtitle>ai anthropic</NodeSubtitle>
                  <NodeParam>model: claude-sonnet-4</NodeParam>
                </AgentNode>
                <AgentNode
                  bg="rgba(100, 200, 255, 0.08)"
                  border="rgba(100, 200, 255, 0.3)"
                  accentColor="#64c8ff"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <NodeTitle>Gemini (Google)</NodeTitle>
                  <NodeSubtitle>ai gemini</NodeSubtitle>
                  <NodeParam>model: gemini-2.5-pro</NodeParam>
                </AgentNode>
                <AgentNode
                  bg="rgba(100, 220, 150, 0.08)"
                  border="rgba(100, 220, 150, 0.3)"
                  accentColor="#64dc96"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <NodeTitle>GPT (OpenAI)</NodeTitle>
                  <NodeSubtitle>ai openai</NodeSubtitle>
                  <NodeParam>model: gpt-5.2</NodeParam>
                </AgentNode>
                <AgentNode
                  bg="rgba(200, 120, 255, 0.08)"
                  border="rgba(200, 120, 255, 0.3)"
                  accentColor="#c878ff"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <NodeTitle>Grok (xAI)</NodeTitle>
                  <NodeSubtitle>ai grok</NodeSubtitle>
                  <NodeParam>model: grok-4</NodeParam>
                </AgentNode>
              </AgentColumn>

              <ArrowSection>
                <ArrowRight size={28} />
              </ArrowSection>

              <ConsensusNode
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <Star size={24} color="#f59e0b" fill="#f59e0b" style={{ marginBottom: 8 }} />
                <ConsensusTitle>Consensus Agent</ConsensusTitle>
                <ConsensusSubtitle>Consensus Judge</ConsensusSubtitle>
              </ConsensusNode>

              <ArrowSection>
                <ArrowRight size={28} />
              </ArrowSection>

              <AgentNode
                bg="rgba(0, 151, 178, 0.1)"
                border="rgba(0, 151, 178, 0.4)"
                accentColor="#0097B2"
                style={{ minWidth: 190 }}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <NodeTitle>Bybit Exchange</NodeTitle>
                <NodeSubtitle>exchange bybit</NodeSubtitle>
                <NodeParam>side: buy_sell</NodeParam>
                <NodeParam style={{ borderTop: 'none', marginTop: 2, paddingTop: 0 }}>leverage: 3</NodeParam>
              </AgentNode>
            </FlowDiagram>

            <FeaturesGrid>
              {features.map((f, i) => (
                <FeatureCard
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <FeatureIconWrap>{f.icon}</FeatureIconWrap>
                  <FeatureTitle>{f.title}</FeatureTitle>
                  <FeatureDesc>{f.desc}</FeatureDesc>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </Container>
        </ArchSection>

        <ProCTASection>
          <Container>
            <ProBadge
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              style={{ margin: '0 auto 24px' }}
            >
              <Star size={12} fill="#000" />
              Available on Pro Plans
            </ProBadge>
            <ProCTATitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              Ready to trade smarter?
            </ProCTATitle>
            <ProCTASubtitle>
              Upgrade to a Pro plan and unleash the power of 4 AI models working in consensus to maximize your trading edge.
            </ProCTASubtitle>
            <CTAGroup style={{ justifyContent: 'center' }}>
              <PrimaryButton
                href="https://f01i.app/login"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Get Started with Pro
                <ArrowRight size={18} />
              </PrimaryButton>
              <SecondaryButton
                as={Link}
                to="/pricing"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                View Pricing
              </SecondaryButton>
            </CTAGroup>
          </Container>
        </ProCTASection>
      </PageContainer>
    </>
  );
};

export default ConsensusAgentPage;
